// Validation des données de propriété
export type PropertyStatus = 'for-sale' | 'for-rent' | 'for-short-rent' | 'sold' | 'rented' | 'archived' | 'estimation';

export const VALID_PROPERTY_STATUSES: PropertyStatus[] = [
  'for-sale',
  'for-rent', 
  'for-short-rent',
  'sold',
  'rented',
  'archived',
  'estimation'
];

/**
 * Valide et normalise le statut d'une propriété
 * @param status - Le statut à valider
 * @param transactionType - Le type de transaction pour déterminer le statut par défaut
 * @param isEstimation - Si c'est une estimation
 * @returns Un statut valide
 */
export const validatePropertyStatus = (
  status: string | undefined,
  transactionType: string | undefined,
  isEstimation: boolean = false
): PropertyStatus => {
  // Si c'est une estimation, forcer le statut à 'estimation'
  if (isEstimation) {
    return 'estimation';
  }

  // Si le statut est fourni et valide, l'utiliser
  if (status && VALID_PROPERTY_STATUSES.includes(status as PropertyStatus)) {
    return status as PropertyStatus;
  }

  // Sinon, déterminer le statut basé sur le type de transaction
  switch (transactionType) {
    case 'sale':
      return 'for-sale';
    case 'rent':
      return 'for-rent';
    case 'short-rent':
      return 'for-short-rent';
    default:
      return 'for-sale'; // Valeur par défaut sécurisée
  }
};

/**
 * Valide les données d'une propriété avant envoi à l'API
 * @param data - Les données de la propriété
 * @param isEdit - Si true, préserve le status existant sans le recalculer
 * @returns Les données validées
 */
export const validatePropertyData = (data: any, isEdit: boolean = false): any => {
  // En mode édition, préserver le status existant s'il est valide
  const validatedStatus = isEdit && data.status && VALID_PROPERTY_STATUSES.includes(data.status as PropertyStatus)
    ? data.status as PropertyStatus
    : validatePropertyStatus(
        data.status,
        data.transactionType,
        data.isEstimation
      );

  return {
    ...data,
    status: validatedStatus,
    // S'assurer que les valeurs numériques sont bien des nombres
    price: Number(data.price) || 0,
    net_price: Number(data.net_price) || 0,
    commission: Number(data.commission) || 0,
    area: Number(data.area) || 0,
    rooms: Number(data.rooms) || 1,
    bedrooms: Number(data.bedrooms) || 0,
    floor: data.floor ? Number(data.floor) : null,
    construction_year: data.construction_year ? Number(data.construction_year) : null,
    // S'assurer que les booléens sont bien des booléens
    has_elevator: Boolean(data.has_elevator),
    has_parking: Boolean(data.has_parking),
    has_balcony: Boolean(data.has_balcony),
    has_terrace: Boolean(data.has_terrace),
    has_air_conditioning: Boolean(data.has_air_conditioning),
    is_published: Boolean(data.is_published),
  };
};